import React, { useEffect, useMemo, useState } from "react";
import { useGlobalContext } from "../context/globalContext";
import { useTheme } from "../context/ThemeContext";
import { IOrders, IShippingAddress } from "../utils/interface";
import { CheckCircle, XCircle, Clock, RefreshCcw } from "lucide-react";

const OrdersPage: React.FC = () => {
  const { orders, getOrders, approveOrder, declineOrder, updateOrderStatus } = useGlobalContext();
  const { dark } = useTheme();
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [countdown, setCountdown] = useState(15);
  const ordersPerPage = 20;
  const [selectedOrder, setSelectedOrder] = useState<IOrders | null>(null);
  const [showModal, setShowModal] = useState(false);

/* ===============================
     🔁 AUTO REFRESH WITH COUNTDOWN
  =============================== */


  const refreshOrders = async () => {
    try {
      const response = await getOrders();
    } catch (err) {
      console.error("Auto refresh failed");
    }
  };
  useEffect(() => {
    const interval = setInterval(async () => {
      setCountdown((prev) => {
        if (prev === 1) {
          refreshOrders();
          return 15;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  
  /* ===============================
     🔹 INITIAL LOAD
  =============================== */
useEffect(() => {
  const loadOrders = async () => {
    setLoading(true);
    await getOrders();
    setLoading(false);
  };
  loadOrders();
}, []); // ✅ REMOVE getOrders


  // 🔹 Sort by payment status and created date
  const sortedOrders = useMemo(() => {
    const orderRank = (status: string) => {
      switch (status?.toLowerCase()) {
        case "pending":
          return 0;
        case "approved":
          return 1;
        case "rejected":
          return 2;
        default:
          return 3;
      }
    };

    return [...(orders || [])].sort((a, b) => {
      const rankDiff = orderRank(a.paymentStatus) - orderRank(b.paymentStatus);
      if (rankDiff !== 0) return rankDiff;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [orders]);

  // 🔹 Pagination
  const indexOfLast = currentPage * ordersPerPage;
  const indexOfFirst = indexOfLast - ordersPerPage;
  const currentOrders = sortedOrders.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(sortedOrders.length / ordersPerPage);

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  // 🔹 Manual Refresh
  const handleRefresh = async () => {
    setLoading(true);
    try {
      await getOrders();
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Payment status badge color
  const statusClass = (status: string) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return "badge bg-success";
      case "pending":
        return "badge bg-warning text-dark";
      case "rejected":
        return "badge bg-danger";
      default:
        return "badge bg-secondary";
    }
  };

  // 🔹 Counts
  const approvedCount =
    orders?.filter((o) => o.paymentStatus?.toLowerCase() === "approved").length || 0;
  const pendingCount =
    orders?.filter((o) => o.paymentStatus?.toLowerCase() === "pending").length || 0;
  const declinedCount =
    orders?.filter((o) => o.paymentStatus?.toLowerCase() === "rejected").length || 0;

  // 🔹 Theme-based styles
  const bgClass = dark ? "bg-dark text-light" : "bg-light text-dark";
  const cardClass = dark
    ? "bg-secondary text-light border-0 shadow-sm"
    : "bg-white text-dark border-0 shadow-sm";
  const tableHeaderClass = dark ? "table-dark" : "table-light";

  
// ✅ FIXED: Removed duplicate "delivered"
  const orderStatuses = [
    "processing",
    "shipped",
    "delivered",
    "cancelled",
  ];

  return (
    <div className={`container-fluid py-4 min-vh-100 ${bgClass}`}>
      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="fw-bold">Orders</h3>
        <div className="d-flex align-items-center gap-3">
          <span className="small text-muted">
            Auto refresh in {countdown}s
          </span>

          <button
            className="btn btn-outline-secondary btn-sm d-flex align-items-center"
            onClick={getOrders}
          >
            <RefreshCcw size={16} className="me-1" />
            Refresh
          </button>
        </div>
      </div>

      {/* STATUS SUMMARY */}
      <div className="row g-4 mb-4">
        <div className="col-md-4">
          <div className={`${cardClass} text-center p-4 h-100 rounded-4`}>
            <Clock size={32} className="text-warning mx-auto mb-2" />
            <h6 className="fw-semibold text-muted">Pending Orders</h6>
            <h4 className="fw-bold mb-0">{pendingCount}</h4>
          </div>
        </div>

        <div className="col-md-4">
          <div className={`${cardClass} text-center p-4 h-100 rounded-4`}>
            <CheckCircle size={32} className="text-success mx-auto mb-2" />
            <h6 className="fw-semibold text-muted">Approved Orders</h6>
            <h4 className="fw-bold mb-0">{approvedCount}</h4>
          </div>
        </div>

        <div className="col-md-4">
          <div className={`${cardClass} text-center p-4 h-100 rounded-4`}>
            <XCircle size={32} className="text-danger mx-auto mb-2" />
            <h6 className="fw-semibold text-muted">Declined Orders</h6>
            <h4 className="fw-bold mb-0">{declinedCount}</h4>
          </div>
        </div>
      </div>

      {/* ORDER TABLE */}
      <div className={`${cardClass} rounded-4`}>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-secondary" role="status" />
              <p className="mt-3 text-muted">Loading orders...</p>
            </div>
          ) : currentOrders.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <p>No orders found.</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table
                  className={`table table-hover align-middle ${
                    dark ? "table-dark table-striped" : "table-striped"
                  }`}
                >
                  <thead className={tableHeaderClass}>
                    <tr>
                      <th>#</th>
                      <th>User</th>
                      <th>Phone</th>
                      <th>Address</th>
                      <th>Items</th>
                      <th>Total</th>
                      <th>Payment Status</th>
                      <th>Order Status</th>
                      <th>Date</th>
                      
                    </tr>
                  </thead>
                  <tbody>
                    {currentOrders.map((o, idx) => {
                      const addr =
                        typeof o.shippingAddress === "object"
                          ? `${(o.shippingAddress as IShippingAddress).address}, ${
                              (o.shippingAddress as IShippingAddress).state
                            }`
                          : o.shippingAddress;

                      const items = (o as any).items || [];
                      const itemText = items
                        .map(
                          (i: any, n: number) =>
                            `${n + 1}. ${i.productName || i.name} — ₦${i.price.toLocaleString()} × ${
                              i.quantity
                            }`
                        )
                        .join("\n");

                      return (
                        <tr
                          key={o._id}
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setSelectedOrder(o);
                            setShowModal(true);
                          }}
                        >
                          <td>{indexOfFirst + idx + 1}</td>
                          <td className="fw-semibold text-capitalize">
                            {o.username || "Unknown"}
                          </td>
                          <td>{o.phoneNumber || "N/A"}</td>
                          <td
                            title={addr}
                            className="text-truncate"
                            style={{ maxWidth: "220px" }} 
                          >
                            {addr}
                          </td>
                          <td>
                            <span
                              className="badge bg-info text-dark"
                              title={itemText}
                              style={{ cursor: "pointer" }}
                            >
                              {items.length} items
                            </span>
                          </td>
                          <td className="fw-bold text-success">
                            ₦{o.totalAmount.toLocaleString()}
                          </td>

                          {/* ===============================
                                💳 PAYMENT STATUS
                            ================================ */}
                            <td onClick={(e) => e.stopPropagation()}>
                              {o.paymentStatus?.toLowerCase() === "approved" ||
                              o.paymentStatus?.toLowerCase() === "rejected" ? (
                                <span className={statusClass(o.paymentStatus)}>
                                  {o.paymentStatus}
                                </span>
                              ) : (
                                <div className="dropdown"
                                onClick={(e) => e.stopPropagation()}>
                                  <button
                                    className="btn btn-sm btn-warning dropdown-toggle"
                                    data-bs-toggle="dropdown"
                                  >
                                    Pending
                                  </button>
                                  <ul className="dropdown-menu" 
                                  onClick={(e) => e.stopPropagation()}>
                                    <li>
                                      <button
                                        className="dropdown-item text-success"
                                        onClick={() => approveOrder(o._id)}
                                      >
                                        Approve
                                      </button>
                                    </li>
                                    <li>
                                      <button
                                        className="dropdown-item text-danger"
                                        onClick={() => declineOrder(o._id)}
                                      >
                                        Decline
                                      </button>
                                    </li>
                                  </ul>
                                </div>
                              )}
                            </td>

                            {/* ===============================
                                📦 ORDER STATUS DROPDOWN
                            ================================ */}
                            <td  onClick={(e) => e.stopPropagation()}>
                              <select
                                className="form-select form-select-sm"
                                value={o.orderStatus || "processing"}
                                disabled={o.paymentStatus?.toLowerCase() !== "approved"}
                                onClick={(e) => e.stopPropagation()} // ✅ prevent modal
                                onChange={(e) =>
                                  updateOrderStatus(o._id, e.target.value)
                                }
                              >
                                {orderStatuses.map((status) => (
                                  <option key={status} value={status}>
                                    {status}
                                  </option>
                                ))}
                              </select>
                            </td>

                            {/* DATE */}
                            <td>
                              {new Date(o.createdAt).toLocaleDateString()}
                            </td>
     
                          
                          
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* PAGINATION */}
              {totalPages > 1 && (
                <nav className="d-flex justify-content-center mt-3">
                  <ul className="pagination pagination-sm mb-0">
                    <li
                      className={`page-item ${
                        currentPage === 1 ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => goToPage(currentPage - 1)}
                      >
                        Prev
                      </button>
                    </li>
                    {[...Array(totalPages)].map((_, i) => (
                      <li
                        key={i}
                        className={`page-item ${
                          currentPage === i + 1 ? "active" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => goToPage(i + 1)}
                        >
                          {i + 1}
                        </button>
                      </li>
                    ))}
                    <li
                      className={`page-item ${
                        currentPage === totalPages ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => goToPage(currentPage + 1)}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              )}
            </>
          )}
        </div>
      </div>

      {showModal && selectedOrder && (() => {

        // ✅ NEW: Calculate items subtotal from products
        const itemsTotal = (selectedOrder as any).items?.reduce(
          (acc: number, item: any) => acc + item.price * item.quantity,
          0
        ) || 0;

        // ✅ NEW: Dynamically calculate shipping (Total - Items)
        const shippingCost = Math.max(
          selectedOrder.totalAmount - itemsTotal,
          0 // prevents negative shipping
        );

        return (
          <div
            className="modal fade show d-block"
            tabIndex={-1}
            style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
          >
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content rounded-4 shadow-lg">

                {/* HEADER */}
                <div className="modal-header">
                  <h5 className="modal-title fw-bold">
                    🧾 Order Receipt
                  </h5>
                  <button
                    className="btn-close"
                    onClick={() => setShowModal(false)}
                  />
                </div>

                {/* BODY */}
                <div className="modal-body px-4 py-3">

                  <div className="text-center mb-4">
                    <h4 className="fw-bold">Your Store Name</h4>
                    <p className="text-muted mb-0">
                      Order ID: {selectedOrder._id}
                    </p>
                    <p className="text-muted">
                      {new Date(selectedOrder.createdAt).toLocaleString()}
                    </p>
                  </div>

                  <hr />

                  {/* CUSTOMER INFO */}
                  <div className="mb-3">
                    <h6 className="fw-bold">Customer Information</h6>
                    <p className="mb-1">
                      <strong>Name:</strong> {selectedOrder.username}
                    </p>
                    <p className="mb-1">
                      <strong>Phone:</strong> {selectedOrder.phoneNumber}
                    </p>
                  </div>

                  {/* SHIPPING ADDRESS */}
                  <div className="mb-3">
                    <h6 className="fw-bold">Shipping Address</h6>
                    {typeof selectedOrder.shippingAddress === "object" ? (
                      <>
                        <p className="mb-1">
                          {(selectedOrder.shippingAddress as IShippingAddress).address}
                        </p>
                        <p className="mb-1">
                          {(selectedOrder.shippingAddress as IShippingAddress).state}
                        </p>
                      </>
                    ) : (
                      <p>{selectedOrder.shippingAddress}</p>
                    )}
                  </div>

                  <hr />

                  {/* ITEMS TABLE */}
                  <h6 className="fw-bold mb-3">Items</h6>

                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Product</th>
                        <th>Qty</th>
                        <th>Price</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedOrder as any).items?.map((item: any, i: number) => (
                        <tr key={i}>
                          <td>{i + 1}</td>
                          <td>{item.productName || item.name}</td>
                          <td>{item.quantity}</td>
                          <td>₦{item.price.toLocaleString()}</td>
                          <td>
                            ₦{(item.price * item.quantity).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <hr />

                  {/* ✅ UPDATED: Proper subtotal using calculated itemsTotal */}
                  <div className="d-flex justify-content-between">
                    <span>Subtotal:</span>
                    <span>₦{itemsTotal.toLocaleString()}</span>
                  </div>

                  {/* ✅ UPDATED: Shipping calculated dynamically */}
                  <div className="d-flex justify-content-between">
                    <span>Shipping:</span>
                    <span>₦{shippingCost.toLocaleString()}</span>
                  </div>

                  <hr />

                  {/* TOTAL (unchanged logic, just cleaner display) */}
                  <div className="d-flex justify-content-between">
                    <h5 className="fw-bold">Total</h5>
                    <h5 className="fw-bold text-success">
                      ₦{selectedOrder.totalAmount.toLocaleString()}
                    </h5>
                  </div>

                  {/* STATUS BADGES */}
                  <div className="mt-3">
                    <span className={`badge ${statusClass(selectedOrder.paymentStatus)}`}>
                      Payment: {selectedOrder.paymentStatus}
                    </span>
                    <span className="badge bg-primary ms-2">
                      Order: {selectedOrder.orderStatus}
                    </span>
                  </div>

                </div>

                {/* FOOTER */}
                <div className="modal-footer">

                  {/* ✅ UPDATED: WhatsApp message now includes items + shipping breakdown */}
                  <button
                    className="btn btn-success"
                    onClick={() => {

                      const itemsList = (selectedOrder as any).items
                        ?.map((item: any, i: number) =>
                          `${i + 1}. ${item.productName || item.name} x${item.quantity} - ₦${(item.price * item.quantity).toLocaleString()}`
                        )
                        .join("\n");

                      const message = `
      🧾 *ORDER RECEIPT*

      Order ID: ${selectedOrder._id}
      Date: ${new Date(selectedOrder.createdAt).toLocaleString()}

      Customer: ${selectedOrder.username}
      Phone: ${selectedOrder.phoneNumber}

      -------------------------
      Items:
      ${itemsList}

      Subtotal: ₦${itemsTotal.toLocaleString()}
      Shipping: ₦${shippingCost.toLocaleString()}
      Total: ₦${selectedOrder.totalAmount.toLocaleString()}

      Payment: ${selectedOrder.paymentStatus}
      Order Status: ${selectedOrder.orderStatus}
                      `;

                      const encodedMessage = encodeURIComponent(message);
                      window.open(`https://wa.me/?text=${encodedMessage}`, "_blank");
                    }}
                  >
                    Share to WhatsApp
                  </button>

                  <button
                    className="btn btn-dark"
                    onClick={() => setShowModal(false)}
                  >
                    Close
                  </button>

                </div>

              </div>
            </div>
          </div>
        );

      })()}
    </div>
  );
};

export default OrdersPage;







